/*
 * Naughty Hut home, option I "Noir Boudoir" (nh-i-* sections).
 * One deferred file, one custom element per interactive section.
 * All content is server-rendered in Liquid; this file only adds behaviour.
 * Motion One is loaded from jsDelivr as progressive enhancement; every
 * effect has a CSS fallback and respects prefers-reduced-motion.
 * Only transform and opacity are animated.
 */
(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.add('nhi-js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const hasIO = 'IntersectionObserver' in window;

  /**
   * Observe elements; calls fn(el, isIntersecting, observer).
   * @param {Element[]} els
   * @param {(el: Element, on: boolean, o?: IntersectionObserver) => void} fn
   * @param {IntersectionObserverInit} [opts]
   * @returns {IntersectionObserver | null}
   */
  function observe(els, fn, opts) {
    if (!hasIO) {
      for (const el of els) fn(el, true);
      return null;
    }
    const io = new IntersectionObserver((entries, o) => {
      for (const entry of entries) fn(entry.target, entry.isIntersecting, o);
    }, opts || {});
    for (const el of els) io.observe(el);
    return io;
  }

  /** @type {Promise<any> | null} */
  let motionPromise = null;
  /** Lazily import Motion One (ESM) after the page has loaded. */
  function loadMotion() {
    if (reduceMotion.matches) return Promise.resolve(null);
    if (!motionPromise) {
      motionPromise = import('https://cdn.jsdelivr.net/npm/motion@12.43.0/+esm').catch(() => null);
    }
    return motionPromise;
  }
  /** Resolves after window load + idle so Motion never competes with LCP. */
  const afterLoad = new Promise((resolve) => {
    const go = () => setTimeout(resolve, 200);
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  });

  /* ------------------------------------------------------------------ */
  /* Generic reveal + blur-to-sharp (runs for every .nhi section)        */
  /* ------------------------------------------------------------------ */
  class NhiSection extends HTMLElement {
    connectedCallback() {
      const reveals = [...this.querySelectorAll('.nhi-rv')];
      this.io = observe(reveals, (el, on, o) => {
        if (!on) return;
        el.classList.add('is-in');
        o?.unobserve(el);
      }, { rootMargin: '0px 0px -4% 0px' });

      for (const img of this.querySelectorAll('.nhi-blur__img')) {
        const done = () => img.classList.add('is-loaded');
        if (img.complete && img.naturalWidth) done();
        else {
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        }
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Hero: crossfade with pause, parallax, candle cursor                  */
  /* ------------------------------------------------------------------ */
  class NhiHero extends NhiSection {
    connectedCallback() {
      super.connectedCallback();
      this.slides = [...this.querySelectorAll('.nhi-hero__img')];
      this.dots = [...this.querySelectorAll('.nhi-hero__dot')];
      this.label = this.querySelector('.nhi-hero__label');
      this.pauseBtn = this.querySelector('.nhi-hero__pause');
      this.current = 0;
      this.visible = true;
      this.paused = reduceMotion.matches;
      this.interval = Number(this.dataset.interval || 6) * 1000;

      if (this.slides.length > 1) {
        this.pauseBtn?.addEventListener('click', () => this.setPaused(!this.paused));
        this.setPaused(this.paused);
        this.#armRotation();
        this.addEventListener('focusin', () => { this.hovering = true; });
        this.addEventListener('focusout', () => { this.hovering = false; });
      }

      this.visIO = observe([this], (el, on) => {
        this.visible = on;
        this.classList.toggle('is-offscreen', !on);
      });

      this.#candle();
      afterLoad.then(() => {
        this.classList.add('is-ready');
        this.#motion();
      });
    }

    /**
     * Start the crossfade only after the first interaction (Chrome stops
     * recording LCP on input), or 8s after load as a fallback. Rotating
     * earlier let a later slide replace slide 1 as the LCP element (28 s LCP).
     */
    #armRotation() {
      const events = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel'];
      const opts = { passive: true, capture: true };
      this.startRotation = () => {
        if (this.timer) return;
        for (const type of events) window.removeEventListener(type, this.startRotation, opts);
        clearTimeout(this.armTimer);
        this.timer = setInterval(() => this.tick(), this.interval);
      };
      for (const type of events) window.addEventListener(type, this.startRotation, opts);
      afterLoad.then(() => { this.armTimer = setTimeout(this.startRotation, 8000); });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      clearInterval(this.timer);
      clearTimeout(this.armTimer);
      this.visIO?.disconnect();
      this.abort?.abort();
    }

    /** @param {boolean} paused */
    setPaused(paused) {
      this.paused = paused;
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-pressed', String(paused));
      this.pauseBtn.setAttribute('aria-label', paused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
    }

    tick() {
      if (this.paused || this.hovering || !this.visible || document.hidden) return;
      this.go((this.current + 1) % this.slides.length);
    }

    /**
     * Slide 0 is the LCP image and is never faded; later slides stack over it.
     * @param {number} index
     */
    go(index) {
      this.slides.forEach((img, i) => {
        if (i === 0) return;
        if (i <= index) {
          img.classList.add('is-active');
        } else if (index === 0 && i < this.slides.length - 1) {
          // jump back: drop middle slides instantly, fade the top one out
          img.style.transition = 'none';
          img.classList.remove('is-active');
          void img.offsetWidth;
          img.style.transition = '';
        } else {
          img.classList.remove('is-active');
        }
      });
      this.current = index;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (this.label) this.label.textContent = (this.dataset.labels || '').split('|')[index] || '';
    }

    #candle() {
      const candle = this.querySelector('.nhi-hero__candle');
      if (!candle || !finePointer.matches || reduceMotion.matches) return;
      this.abort = new AbortController();
      let x = 0; let y = 0; let raf = 0;
      window.addEventListener('pointermove', (e) => {
        x = e.clientX; y = e.clientY;
        if (!this.visible) { candle.classList.remove('is-on'); return; }
        candle.classList.add('is-on');
        if (raf) return;
        raf = requestAnimationFrame(() => {
          candle.style.transform = `translate3d(${x}px, ${y}px, 0)`;
          raf = 0;
        });
      }, { passive: true, signal: this.abort.signal });
      document.addEventListener('pointerleave', () => candle.classList.remove('is-on'), { signal: this.abort.signal });
    }

    async #motion() {
      const m = await loadMotion();
      if (!m) return;
      root.classList.add('nhi-m');
      const media = this.querySelector('.nhi-hero__media');
      const copy = this.querySelector('.nhi-hero__copy');
      const wide = window.matchMedia('(min-width: 750px)').matches; // phones: photo and copy are stacked, no scroll-linked fade
      if (media && wide) m.scroll(m.animate(media, { transform: ['translateY(0px) scale(1)', 'translateY(70px) scale(1.06)'] }, { ease: 'linear' }), { target: this, offset: ['start start', 'end start'] });
      if (copy && wide) m.scroll(m.animate(copy, { opacity: [1, 0], transform: ['translateY(0px)', 'translateY(-50px)'] }, { ease: 'linear' }), { target: this, offset: ['center center', 'end start'] });
      if (finePointer.matches) {
        for (const btn of this.querySelectorAll('.nhi-btn')) {
          btn.addEventListener('pointermove', (e) => {
            const r = btn.getBoundingClientRect();
            m.animate(btn, { transform: `translate(${(e.clientX - r.left - r.width / 2) * 0.22}px, ${(e.clientY - r.top - r.height / 2) * 0.32}px)` }, { type: 'spring', stiffness: 200, damping: 15 });
          });
          btn.addEventListener('pointerleave', () => m.animate(btn, { transform: 'translate(0px, 0px)' }, { type: 'spring', stiffness: 150, damping: 12 }));
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Her / Him / Us curtains + duotone-to-colour                          */
  /* ------------------------------------------------------------------ */
  class NhiPanels extends NhiSection {
    connectedCallback() {
      super.connectedCallback();
      this.panelIO = observe([...this.querySelectorAll('.nhi-pn')], (el, on, o) => {
        if (!on) return;
        el.classList.add('is-in');
        setTimeout(() => el.classList.add('is-warm'), reduceMotion.matches ? 0 : 1500);
        o?.unobserve(el);
      }, { threshold: 0.12 });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.panelIO?.disconnect();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Product grids: add to cart through the theme's own cart flow         */
  /* (POST theme.routes.cartAdd, then the Impulse "ajaxProduct:added"     */
  /* event, which rebuilds and opens the theme's cart drawer).            */
  /* ------------------------------------------------------------------ */
  class NhiProducts extends NhiSection {
    connectedCallback() {
      super.connectedCallback();
      this.status = this.querySelector('.nhi-status');
      this.onSubmit = this.#onSubmit.bind(this);
      this.addEventListener('submit', this.onSubmit);
      afterLoad.then(() => this.#motion());
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('submit', this.onSubmit);
      this.controller?.abort();
    }

    /** @param {string} message */
    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      requestAnimationFrame(() => { this.status.textContent = message; });
    }

    /** @param {SubmitEvent} event */
    async #onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-nhi-atc]')) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      if (button?.getAttribute('aria-busy') === 'true') return;
      button?.setAttribute('aria-busy', 'true');
      this.controller = new AbortController();

      const url = window.theme?.routes?.cartAdd || '/cart/add.js';
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: new FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          signal: this.controller.signal,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.announce(data.description || data.message || form.dataset.errorText || 'Sorry, that could not be added.');
          return;
        }
        this.announce(form.dataset.addedText || 'Added to cart');
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: data, addToCartBtn: button },
          bubbles: true,
        }));
        if (window.theme?.settings?.cartType === 'page') window.location.href = window.theme.routes.cartPage;
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Network failure: fall back to a normal form post
          form.submit();
        }
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }

    async #motion() {
      const grid = this.querySelector('.nhi-grid');
      if (!grid) return;
      const m = await loadMotion();
      if (!m) return;
      root.classList.add('nhi-m');
      const cards = [...grid.querySelectorAll('.nhi-rise')];
      if (!cards.length) return;
      const rect = grid.getBoundingClientRect();
      if (rect.top < window.innerHeight) { for (const c of cards) c.classList.add('is-up'); return; }
      // Final fix 2026-09-29 (R3D-11): reveal as the grid's first pixel approaches (200px early), never a blank band
      // while the shopper waits for 15% of the section; shorter rise so it reads as settled, not late.
      m.inView(grid, () => {
        m.animate(cards, { opacity: [0, 1], transform: ['translateY(24px)', 'translateY(0px)'] }, { delay: m.stagger(0.05), duration: 0.6, ease: [0.22, 1, 0.36, 1] });
        for (const c of cards) c.classList.add('is-up');
      }, { amount: 0, margin: '0px 0px 200px 0px' });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Finder: toggles server-rendered result lists                         */
  /* ------------------------------------------------------------------ */
  class NhiFinder extends NhiSection {
    connectedCallback() {
      super.connectedCallback();
      this.state = { who: this.dataset.who, mood: this.dataset.mood };
      this.results = [...this.querySelectorAll('.nhi-match__result')];
      this.fallback = this.querySelector('[data-nhi-fallback]');
      this.onClick = this.#onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('click', this.onClick);
    }

    /** @param {MouseEvent} event */
    #onClick(event) {
      const chip = event.target.closest('.nhi-chip');
      if (!chip) return;
      const group = chip.closest('[data-key]');
      for (const c of group.querySelectorAll('.nhi-chip')) c.setAttribute('aria-pressed', String(c === chip));
      this.state[group.dataset.key] = chip.dataset.value;
      this.#show();
    }

    #show() {
      let found = false;
      for (const list of this.results) {
        const match = list.dataset.who === this.state.who && list.dataset.mood === this.state.mood;
        list.hidden = !match || found;
        if (match) found = true;
      }
      if (this.fallback) this.fallback.hidden = found;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Sticky story: active chapter + scroll-scaled imagery                 */
  /* ------------------------------------------------------------------ */
  class NhiStory extends NhiSection {
    connectedCallback() {
      super.connectedCallback();
      this.stage = this.querySelector('.nhi-story__stage');
      this.images = [...this.querySelectorAll('.nhi-story__img')];
      this.steps = [...this.querySelectorAll('.nhi-story__step')];
      this.stepIO = observe(this.steps, (el, on) => {
        if (!on) return;
        const i = Number(el.dataset.chapter);
        for (const s of this.steps) s.classList.toggle('is-active', s === el);
        if (this.stage) this.stage.dataset.chapter = String(i);
        this.images.forEach((img, k) => img.classList.toggle('is-active', k === i));
      }, { rootMargin: '-45% 0px -45% 0px' });
      afterLoad.then(() => this.#motion());
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.stepIO?.disconnect();
    }

    async #motion() {
      const scale = this.querySelector('.nhi-story__scale');
      const story = this.querySelector('.nhi-story');
      if (!scale || !story) return;
      const m = await loadMotion();
      if (!m) return;
      m.scroll(m.animate(scale, { transform: ['scale(1.1)', 'scale(1)'] }, { ease: 'linear' }), { target: story, offset: ['start end', 'end end'] });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Brand marquee: CSS animation, paused off-screen and for reduced motion */
  /* ------------------------------------------------------------------ */
  class NhiMarquee extends NhiSection {
    connectedCallback() {
      super.connectedCallback();
      const mq = this.querySelector('.nhi-mq');
      if (!mq) return;
      this.mqIO = observe([mq], (el, on) => el.classList.toggle('is-running', on && !reduceMotion.matches));
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.mqIO?.disconnect();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Find your match quiz: steps, progress, scoring and results.          */
  /* Every product card is server-rendered; this only filters and orders. */
  /* ------------------------------------------------------------------ */
  /* ---------------------------------------------------------------------
   * Find your match quiz. Loads a per-audience catalogue index
   * (assets/nh-quiz-*.json) after the first answer, then filters, scores,
   * diversifies and renders matches. Index row keys:
   * h handle, t title, v vendor index, p price (cents), i image, c category,
   * n intensity 1-3, b beginner, a advanced, s sales score 0-100,
   * x flags (1 bestseller, 2 app, 4 premium brand, 8 silicone, 16 remote).
   * ------------------------------------------------------------------- */
  const QUIZ_GROUPS = {
    her: { air: ['air'], wand: ['wand'], rabbit: ['rabbit'], gspot: ['gspot'], bullet: ['bullet', 'egg', 'clit', 'vibe', 'layon'], wear: ['wear'], dildo: ['dildo', 'thrust', 'strapon'], anal: ['plug', 'beads'], lingerie: ['lingerie'] },
    him: { stroker: ['stroker'], auto: ['autostroker'], ring: ['ring', 'vring'], prostate: ['prostate'], anal: ['plug', 'beads'], sleeve: ['sleeve', 'pump'] },
    couples: { cvibe: ['couples', 'wear'], vring: ['vring'], remote: ['@remote'], bondage: ['bondage', 'impact', 'nipple'], furniture: ['furniture', 'machine'], games: ['game', 'kit'], lube: ['lube', 'massage'] },
    first: { bullet: ['bullet', 'egg', 'clit', 'vibe', 'layon'], air: ['air'], rabbit: ['rabbit', 'gspot'], stroker: ['stroker', 'sleeve'], ring: ['ring', 'vring'], couples: ['couples', 'wear'], anal: ['plug', 'prostate'], lube: ['lube', 'massage'] },
  };
  const QUIZ_NOT_SURPRISE = new Set(['lube', 'massage', 'lingerie', 'game', 'kit', 'games']);
  /* Fix D (2026-09-28): accessories and spare parts are never a "match" (replacement heads, storage cases, adapters, chargers, Versa sleeves). */
  const QUIZ_ACCESSORY = /\breplacement\b|\b(?:climax|spare|stimulator|brush|wand|massage)\s+heads?\b|\battachments?\b|\bcharg(?:er|ing (?:cable|cord|base|dock))s?\b|\bstorage\b|\btoy case\b|\badapt[eo]rs?\b|^femme funn versa [a-z] sleeve\b/i;
  const QUIZ_RELATED = {
    air: ['clit', 'bullet'], wand: ['bullet', 'vibe'], rabbit: ['gspot', 'thrust'], gspot: ['rabbit', 'dildo'], bullet: ['egg', 'clit', 'vibe'],
    egg: ['bullet', 'wear'], clit: ['air', 'bullet'], vibe: ['bullet', 'wand'], layon: ['bullet', 'clit'], wear: ['egg', 'couples'],
    dildo: ['gspot', 'thrust'], thrust: ['rabbit', 'dildo'], strapon: ['dildo'], plug: ['beads', 'prostate'], beads: ['plug'],
    lingerie: ['bondage'], stroker: ['sleeve', 'autostroker'], autostroker: ['stroker'], ring: ['vring'], vring: ['ring', 'couples'],
    prostate: ['plug'], sleeve: ['pump', 'stroker'], pump: ['sleeve'], couples: ['vring', 'wear'], bondage: ['impact', 'nipple'],
    impact: ['bondage'], nipple: ['bondage'], furniture: ['machine'], machine: ['furniture'], game: ['kit'], kit: ['game'], lube: ['massage'], massage: ['lube'],
  };
  const QUIZ_BANDS = { u50: [0, 4999], u100: [5000, 9999], u250: [10000, 25000], o250: [25001, Infinity], any: [0, Infinity] };
  const QUIZ_BAND_ORDER = ['u50', 'u100', 'u250', 'o250'];
  const QUIZ_WHY = {
    air: 'Touch-free air-pulse suction for fast, intense clitoral orgasms', wand: 'Deep, rumbly wand vibrations that relax and satisfy',
    rabbit: 'Dual stimulation: G-spot and clitoris at the same time', gspot: 'Curved to find the G-spot with ease', bullet: 'Small, discreet and surprisingly strong',
    egg: 'Hands-free vibrations wherever you want them', clit: 'Focused clitoral pleasure', vibe: 'An easy all-rounder vibrator', layon: 'Lies flat for effortless, hands-free play',
    wear: 'Wearable, hands-free pleasure solo or together', dildo: 'Satisfying fullness, made to be explored', thrust: 'Thrusting motion that does the work for you',
    strapon: 'Built for strap-on play together', plug: 'Comfortable, tapered and made for anal play', beads: 'Graduated beads for a slow, teasing build',
    lingerie: 'Feel irresistible and set the mood', stroker: 'Realistic texture for a better-than-hand feel', autostroker: 'Automatic strokes: sit back and let it do the work',
    ring: 'Firmer, longer-lasting erections', vring: 'Adds vibration to sex for both of you', prostate: 'Angled for prostate pleasure',
    sleeve: 'Adds girth and texture', pump: 'Boosts blood flow for a firmer feel', couples: 'Designed to be shared during sex', bondage: 'Easy restraint for playful power exchange',
    impact: 'Spanking and sensation play', nipple: 'Nipple play and teasing', furniture: 'Better angles and deeper positions', machine: 'Hands-free, powerful machine play',
    game: 'Fun prompts to spice things up', kit: 'A great-value set to try several toys', lube: 'Smooth, long-lasting glide', massage: 'Sensual massage to set the mood',
    herpump: 'Suction for heightened sensitivity', estim: 'Electro-stimulation for experienced players',
  };
  const QUIZ_TIPS = {
    air: ['Start on the lowest setting and hover rather than press: air-pulse works without direct contact.', 'Tilt the nozzle for a slightly off-centre seal: many find it more intense than straight on.'],
    wand: ['Use a layer of fabric at first: wand vibrations are strong and deep.', 'Pair a wand with a silicone attachment for G-spot play.'],
    rabbit: ['Look for a flexible shaft so the clitoral arm sits where you need it.', 'Try different rhythms for the shaft and arm: independent controls are worth it.'],
    gspot: ['The G-spot is 5 to 7 cm inside on the front wall: a firm, curved tip helps.', 'A firmer, heavier G-spot toy gives more pressure than vibration alone.'],
    bullet: ['A bullet is the easiest first vibrator: small, quiet and easy to hold.', 'Pair a bullet with a cock ring or sleeve for extra versatility.'],
    wear: ['Wearables stay put during sex: practise solo first to find the fit.', 'App control lets a partner tease you from across the room.'],
    dildo: ['Start with a smaller diameter and plenty of water-based lube.', 'Suction-cup bases make hands-free and shower play easy.'],
    anal: ['Anal toys must have a flared base. Go slow and use plenty of thick lube.', 'Graduated sizes and vibrating plugs are the natural next step.'],
    lingerie: ['Choose your usual size; stretch lace forgives a lot.', 'Pair lingerie with light restraints for a full scene.'],
    stroker: ['Use water-based lube and rinse with warm water after every use.', 'A tighter, textured sleeve adds intensity; warm it in warm water first.'],
    auto: ['Automatic strokers are loud-ish: check the noise rating if privacy matters.', 'Pair with interactive content or a partner app for synced play.'],
    ring: ['Start with a stretchy silicone ring and never wear it over 30 minutes.', 'Vibrating rings give both partners something extra during sex.'],
    prostate: ['Relax, go slow and use thick lube: the prostate is 5 cm in, towards the belly.', 'Remote-controlled prostate massagers let a partner take charge.'],
    sleeve: ['Sleeves and pumps work best with water-based lube.', 'Use a pump with a pressure gauge and short sessions.'],
    cvibe: ['Couples vibes sit inside and against the clitoris at once: try it solo first.', 'Pick one with app control for long-distance play.'],
    vring: ['A vibrating ring is the easiest couples toy: no learning curve for either of you.', 'Rechargeable rings are stronger than disposable ones.'],
    remote: ['Remote toys are perfect for teasing: agree a safe word before going out.', 'App control adds long-distance play and custom patterns.'],
    bondage: ['Agree a safe word first and start with soft, easy-release cuffs.', 'Try blindfolds with restraints to heighten every touch.'],
    furniture: ['A wedge makes many positions deeper and more comfortable.', 'Swings and machines open up hands-free play.'],
    games: ['Games are a low-pressure way to talk about what you both want.', 'Kits are great value to discover new favourites.'],
    lube: ['Water-based lube suits every toy. Avoid silicone lube with silicone toys.', 'Silicone lube lasts longest in the shower; oil-based is for massage only.'],
    couples: ['Choose a toy you can both control: rings and wearables are easy wins.', 'Try it solo first so you know what you like together.'],
  };

  const quizEsc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const quizHash = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0) / 4294967296; };

  class NhiQuiz extends NhiSection {
    connectedCallback() {
      super.connectedCallback();
      this.steps = [...this.querySelectorAll('.nhi-quiz__step')];
      this.dots = [...this.querySelectorAll('.nhi-quiz__dot')];
      this.back = this.querySelector('[data-back]');
      this.next = this.querySelector('[data-next]');
      this.results = this.querySelector('[data-results]');
      this.grid = this.querySelector('[data-grid]');
      this.summary = this.querySelector('[data-summary]');
      this.countEl = this.querySelector('[data-count]');
      this.widened = this.querySelector('[data-widened]');
      this.moreBtn = this.querySelector('[data-more-btn]');
      this.sortSel = this.querySelector('[data-sort]');
      this.tip = this.querySelector('[data-tip]');
      this.pairs = this.querySelector('[data-pairs]');
      this.loading = this.querySelector('[data-loading]');
      this.seeAll = this.querySelector('[data-see-all]');
      this.burst = this.querySelector('.nhi-quiz__burst');
      this.status = this.querySelector('.nhi-status');
      try { this.picks = JSON.parse(this.querySelector('[data-picks]')?.textContent || '{}'); } catch { this.picks = {}; }
      this.keys = ['who', 'cats', 'mood', 'budget', 'exp'];
      this.state = { cats: [] };
      this.data = {};
      this.current = 0;
      this.classList.add('is-ready');
      this.onClick = this.#onClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.sortSel?.addEventListener('change', () => { this.shown = 12; this.#render(); });

      const params = new URL(window.location.href).searchParams;
      const who = params.get('who');
      const button = who && this.querySelector(`[data-key="who"] [data-value="${CSS.escape(who)}"]`);
      if (button) {
        this.#choose(button, false);
        const cats = (params.get('cats') || '').split(',').filter(Boolean);
        const group = this.#catGroup();
        for (const c of cats) group?.querySelector(`[data-value="${CSS.escape(c)}"]`)?.setAttribute('aria-pressed', 'true');
        this.#syncCats();
        let ok = this.state.cats.length > 0;
        for (const k of ['mood', 'budget', 'exp']) {
          const b = params.get(k) && this.querySelector(`[data-key="${k}"] [data-value="${CSS.escape(params.get(k))}"]`);
          if (b) this.#choose(b, false); else ok = false;
        }
        if (ok) this.#show(true);
        else this.#goto(1, true);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener('click', this.onClick);
    }

    #catGroup() { return this.querySelector(`[data-key="cats"][data-for="${CSS.escape(this.state.who || '')}"]`); }

    /** @param {MouseEvent} event */
    #onClick(event) {
      const add = event.target.closest('[data-quiz-add]');
      if (add) { this.#add(add); return; }
      const option = event.target.closest('.nhi-opt');
      if (option && this.contains(option)) { this.#choose(option, true); return; }
      if (event.target.closest('[data-next]')) { if (this.state.cats.length) this.#goto(2); return; }
      if (event.target.closest('[data-back]')) { this.#goto(Math.max(0, this.current - 1), false, true); return; }
      if (event.target.closest('[data-more-btn]')) { this.shown += 12; this.#render(this.shown - 12); return; }
      if (event.target.closest('[data-restart]')) {
        this.state = { cats: [] };
        for (const b of this.querySelectorAll('.nhi-opt')) b.setAttribute('aria-pressed', 'false');
        this.results.classList.remove('is-shown');
        try { history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
        this.#goto(0, false, true);
        this.steps[0].scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
      }
    }

    #syncCats() {
      const group = this.#catGroup();
      this.state.cats = group ? [...group.querySelectorAll('.nhi-opt[aria-pressed="true"]')].map((b) => b.dataset.value) : [];
      if (this.next) this.next.disabled = this.state.cats.length === 0;
    }

    /**
     * @param {HTMLElement} option
     * @param {boolean} advance
     */
    #choose(option, advance) {
      const group = option.closest('[data-key]');
      const key = group.dataset.key;
      if (group.hasAttribute('data-multi')) {
        const on = option.getAttribute('aria-pressed') !== 'true';
        const surprise = option.dataset.value === 'surprise';
        for (const b of group.querySelectorAll('.nhi-opt')) {
          if (b === option) b.setAttribute('aria-pressed', String(on));
          else if (on && (surprise || b.dataset.value === 'surprise')) b.setAttribute('aria-pressed', 'false');
        }
        this.#syncCats();
        if (advance && on && surprise) setTimeout(() => this.#goto(2), reduceMotion.matches ? 0 : 220);
        return;
      }
      for (const b of group.querySelectorAll('.nhi-opt')) b.setAttribute('aria-pressed', String(b === option));
      this.state[key] = option.dataset.value;
      if (key === 'who') {
        for (const g of this.querySelectorAll('[data-key="cats"]')) g.hidden = g.dataset.for !== this.state.who;
        this.#syncCats();
        this.#load(this.state.who); // lazy: fetch the index as soon as we know the audience
      }
      if (!advance) return;
      const next = this.current + 1;
      setTimeout(() => (next < this.steps.length ? this.#goto(next) : this.#show()), reduceMotion.matches ? 0 : 220);
    }

    /**
     * @param {number} index
     * @param {boolean} [instant]
     * @param {boolean} [backwards]
     */
    #goto(index, instant, backwards) {
      this.current = index;
      this.steps.forEach((step, i) => {
        step.hidden = i !== index;
        step.classList.remove('is-entering', 'is-entering-back');
        if (i === index && !instant) { void step.offsetWidth; step.classList.add(backwards ? 'is-entering-back' : 'is-entering'); }
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-current', i === index);
        dot.classList.toggle('is-done', i < index);
        if (i === index) dot.setAttribute('aria-current', 'step'); else dot.removeAttribute('aria-current');
      });
      this.back.hidden = index === 0;
      if (!instant) {
        const scope = index === 1 ? this.#catGroup() : this.steps[index];
        const first = scope?.querySelector('.nhi-opt[aria-pressed="true"]') || scope?.querySelector('.nhi-opt');
        first?.focus({ preventScroll: true });
      }
    }

    /** @param {string} who */
    #load(who) {
      if (this.data[who]) return this.data[who];
      const url = this.dataset[`src${who.charAt(0).toUpperCase()}${who.slice(1)}`];
      const key = `nhq:${url}`;
      this.data[who] = (async () => {
        let json = null;
        try { const cached = sessionStorage.getItem(key); if (cached) json = JSON.parse(cached); } catch { /* storage unavailable */ }
        if (!json) {
          const res = await fetch(url, { credentials: 'omit' });
          if (!res.ok) throw new Error(`index ${res.status}`);
          const text = await res.text();
          json = JSON.parse(text);
          try { sessionStorage.setItem(key, text); } catch { /* quota or private mode */ }
        }
        const k = json.k;
        const idx = Object.fromEntries(k.map((name, i) => [name, i]));
        const rows = json.r.map((r) => ({ h: r[idx.h], t: r[idx.t], v: json.v[r[idx.v]] || '', p: r[idx.p], i: r[idx.i], c: r[idx.c], n: r[idx.n], b: r[idx.b], a: r[idx.a], s: r[idx.s], x: r[idx.x] })).filter((row) => !QUIZ_ACCESSORY.test(row.t || ''));
        return { rows, img: json.img };
      })();
      this.data[who].catch(() => { delete this.data[who]; });
      return this.data[who];
    }

    /** Internal categories for the chosen groups */
    #catSet(who, groups) {
      const map = QUIZ_GROUPS[who] || {};
      const keys = groups.includes('surprise') ? Object.keys(map).filter((g) => !QUIZ_NOT_SURPRISE.has(g)) : groups;
      const set = new Set();
      let remote = false;
      for (const g of keys) for (const c of map[g] || []) { if (c === '@remote') remote = true; else set.add(c); }
      return { set, remote };
    }

    async #show(fromUrl) {
      const { who, mood, budget, exp, cats } = this.state;
      this.results.classList.add('is-shown');
      this.dots.forEach((dot) => { dot.classList.remove('is-current'); dot.classList.add('is-done'); dot.removeAttribute('aria-current'); });
      this.grid.replaceChildren();
      this.loading.hidden = false;
      this.moreBtn.hidden = true;
      if (!fromUrl) {
        this.results.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
      this.results.focus({ preventScroll: true });

      let data;
      try { data = await this.#load(who); } catch {
        this.loading.hidden = true;
        this.countEl.textContent = 'Sorry, we could not load matches. Please try again.';
        return;
      }
      this.img = data.img;
      const { set, remote } = this.#catSet(who, cats);
      const toyish = (r) => !QUIZ_NOT_SURPRISE.has(r.c) && r.c !== 'bondage';
      const inCats = (r, s) => s.has(r.c) || (remote && (r.x & 18) && toyish(r));
      let [lo, hi] = QUIZ_BANDS[budget] || QUIZ_BANDS.any;
      let pool = data.rows.filter((r) => inCats(r, set) && r.p >= lo && r.p <= hi);
      let widened = false;
      if (pool.length < 6 && budget !== 'any') {
        const bi = QUIZ_BAND_ORDER.indexOf(budget);
        const nb = [QUIZ_BAND_ORDER[bi - 1], QUIZ_BAND_ORDER[bi + 1]].filter(Boolean);
        lo = Math.min(lo, ...nb.map((b) => QUIZ_BANDS[b][0]));
        hi = Math.max(hi, ...nb.map((b) => QUIZ_BANDS[b][1]));
        pool = data.rows.filter((r) => inCats(r, set) && r.p >= lo && r.p <= hi);
        widened = true;
      }
      if (pool.length < 6) {
        const wide = new Set(set);
        for (const c of set) for (const rc of QUIZ_RELATED[c] || []) wide.add(rc);
        pool = data.rows.filter((r) => inCats(r, wide) && r.p >= lo && r.p <= hi);
        widened = true;
      }

      const target = { gentle: 1, medium: 2, intense: 3 }[mood] || 2;
      for (const r of pool) {
        let sc = 30 - 15 * Math.abs(r.n - target);
        if (exp === 'new') sc += (r.b ? 30 : -8) - (r.a ? 40 : 0);
        else if (exp === 'know') sc += (r.a ? 30 : 0) - (r.b ? 15 : 0) + (r.x & 2 ? 10 : 0) + (r.x & 4 ? 12 : 0) + (r.x & 16 ? 5 : 0);
        else sc += (r.b && !r.a ? 6 : 0) + (r.a ? -4 : 0) + (r.x & 4 ? 5 : 0);
        sc += r.s * 0.35 + (r.x & 1 ? 10 : 0) + (r.x & 4 ? 4 : 0) + (r.x & 8 ? 3 : 0);
        if (Object.prototype.hasOwnProperty.call(this.picks, r.h)) sc += 15;
        if (!set.has(r.c) && !(remote && (r.x & 18))) sc -= 20; // widened category
        if (!r.i) sc -= 50;
        r.score = sc + quizHash(r.h) * 2;
      }
      pool.sort((a, b) => b.score - a.score);
      const nGroups = cats.includes('surprise') ? 4 : cats.length;
      this.ranked = this.#diversify(pool, nGroups > 1 ? Math.max(3, Math.ceil(12 / nGroups)) : 12);
      this.widenedFlag = widened;
      this.shown = 12;
      if (this.sortSel) this.sortSel.value = 'match';
      this.loading.hidden = true;
      this.#render();
      this.#extras(data.rows, set);

      const label = (key) => this.querySelector(`[data-key="${key}"] [data-value="${CSS.escape(this.state[key] || '')}"] .nhi-opt__label`)?.textContent.trim();
      const catLabels = cats.map((c) => this.#catGroup()?.querySelector(`[data-value="${CSS.escape(c)}"] .nhi-opt__label`)?.textContent.trim()).filter(Boolean).join(', ');
      if (this.summary) this.summary.textContent = [label('who'), catLabels, label('mood'), label('budget'), label('exp')].filter(Boolean).join(' · ');
      this.widened.hidden = !widened;
      if (widened) this.widened.textContent = this.dataset.widenedText || '';
      const cap = who.charAt(0).toUpperCase() + who.slice(1);
      const url = this.dataset[`see${cap}`];
      const title = this.dataset[`see${cap}Title`];
      if (this.seeAll && url) { this.seeAll.href = url; const t = this.seeAll.querySelector('[data-see-title]'); if (t && title) t.textContent = title; }
      try {
        const q = new URLSearchParams({ who, cats: cats.join(','), mood, budget, exp });
        history.replaceState(null, '', `${window.location.pathname}?${q}`);
      } catch { /* ignore */ }
      if (this.burst && !reduceMotion.matches) { this.burst.classList.remove('is-burst'); void this.burst.offsetWidth; this.burst.classList.add('is-burst'); }
    }

    /** Greedy diversity per page of 12: max 2 per brand, max 3 per category when several were asked for. */
    #diversify(list, catCap) {
      const out = [];
      let rest = list.slice();
      while (rest.length) {
        const vend = {}; const cat = {}; const page = [];
        const take = (r) => { page.push(r); vend[r.v] = (vend[r.v] || 0) + 1; cat[r.c] = (cat[r.c] || 0) + 1; };
        // pass 1: brand cap 2 + category cap; pass 2: brand cap only; pass 3: anything
        for (const ok of [(r) => (vend[r.v] || 0) < 2 && (cat[r.c] || 0) < catCap, (r) => (vend[r.v] || 0) < 2, () => true]) {
          for (const r of rest) { if (page.length >= 12) break; if (!page.includes(r) && ok(r)) take(r); }
        }
        out.push(...page);
        rest = rest.filter((r) => !page.includes(r));
      }
      return out;
    }

    #sorted() {
      const mode = this.sortSel?.value || 'match';
      const list = this.ranked.slice();
      if (mode === 'best') list.sort((a, b) => (b.x & 1) - (a.x & 1) || b.s - a.s || b.score - a.score);
      else if (mode === 'low') list.sort((a, b) => a.p - b.p);
      else if (mode === 'high') list.sort((a, b) => b.p - a.p);
      return list;
    }

    #render(from = 0) {
      const list = this.#sorted();
      const total = list.length;
      const upto = Math.min(this.shown, total);
      if (from === 0) this.grid.replaceChildren();
      const html = list.slice(from, upto).map((r, i) => this.#card(r, i)).join('');
      this.grid.insertAdjacentHTML('beforeend', html);
      const text = (this.dataset.countText || '').replace('[count]', total.toLocaleString('en-NZ')).replace('[shown]', String(upto));
      this.countEl.textContent = total ? text : 'No exact matches. Try a different budget or add another category.';
      this.moreBtn.hidden = upto >= total;
      if (from > 0) this.grid.children[from]?.querySelector('a.nhi-card__link')?.focus({ preventScroll: false });
    }

    #money(cents) { return `${this.dataset.currency || '$'}${(cents / 100).toFixed(2)}`; }

    #src(r, w) {
      const base = /^https?:/.test(r.i) ? r.i : this.img + r.i;
      return `${base}${base.includes('?') ? '&' : '?'}width=${w}`;
    }

    #why(r) {
      const pick = this.picks[r.h];
      if (pick) return pick;
      const bits = [QUIZ_WHY[r.c] || 'A great match for your answers'];
      if (r.x & 16) bits.push('remote control');
      else if (r.x & 2) bits.push('app-connected');
      if (r.b && this.state.exp !== 'know') bits.push('beginner-friendly');
      else if (r.a && this.state.exp === 'know') bits.push('for experienced play');
      if (r.x & 8 && bits.length < 3) bits.push('body-safe silicone');
      return bits.slice(0, 3).join(' · ');
    }

    #card(r, i) {
      const url = `/products/${encodeURIComponent(r.h)}`;
      const id = `NhiQ-${r.h}`;
      const quarter = this.#money(Math.round(r.p / 4));
      const afterpay = (this.dataset.afterpay || '').replace('[amount]', quarter);
      const badge = r.x & 1 && this.dataset.badge ? `<span class="nhi-card__badge">${quizEsc(this.dataset.badge)}</span>` : '';
      const img = r.i
        ? `<img class="nhi-card__img" src="${quizEsc(this.#src(r, 400))}" srcset="${[300, 400, 600].map((w) => `${quizEsc(this.#src(r, w))} ${w}w`).join(', ')}" sizes="(min-width: 900px) 300px, 45vw" width="600" height="600" loading="lazy" decoding="async" alt="${quizEsc(r.t)}">`
        : '';
      return `<li class="nhi-quiz__item is-in" style="--i:${i % 12}"><article class="nhi-card" aria-labelledby="${quizEsc(id)}">
<a class="nhi-card__stage" href="${url}" tabindex="-1" aria-hidden="true">${img}${badge}</a>
<span class="nhi-card__brand">${quizEsc(r.v)}</span>
<h3 class="nhi-card__title" id="${quizEsc(id)}"><a class="nhi-card__link" href="${url}">${quizEsc(r.t)}</a></h3>
<p class="nhi-card__why">${quizEsc(this.#why(r))}</p>
<div class="nhi-card__row"><div><span class="nhi-card__price">${this.#money(r.p)}</span><span class="nhi-card__afterpay">${quizEsc(afterpay)}</span></div>
<button type="button" class="nhi-card__add" data-quiz-add="${quizEsc(r.h)}" aria-label="Add to bag: ${quizEsc(r.t)}"><svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/></svg></button></div>
</article></li>`;
    }

    /** Expert tip and lube cross-sell */
    #extras(rows, set) {
      const { cats, exp } = this.state;
      const first = cats.find((c) => c !== 'surprise') || 'bullet';
      const tipKey = QUIZ_TIPS[first] ? first : (QUIZ_GROUPS[this.state.who]?.[first]?.[0] || first);
      const tips = QUIZ_TIPS[tipKey];
      const tipText = this.querySelector('[data-tip-text]');
      if (tips && tipText) { tipText.textContent = exp === 'know' ? tips[1] : tips[0]; this.tip.hidden = false; } else this.tip.hidden = true;

      const pairGrid = this.querySelector('[data-pairs-grid]');
      const note = this.querySelector('[data-pairs-note]');
      if (set.has('lube') || !pairGrid) { this.pairs.hidden = true; return; }
      const anal = cats.includes('anal') || cats.includes('prostate');
      const lubes = rows.filter((r) => r.c === 'lube' && r.i && /water/i.test(r.t) && (anal || !/anal/i.test(r.t)));
      const pickL = lubes
        .map((r) => ({ r, k: (anal && /anal/i.test(r.t) ? 50 : 0) + r.s + (r.x & 1 ? 20 : 0) + quizHash(r.h) }))
        .sort((a, b) => b.k - a.k).slice(0, 2).map((o) => o.r);
      if (!pickL.length) { this.pairs.hidden = true; return; }
      note.textContent = anal
        ? 'Anal play needs plenty of lube: a thicker water-based formula is safe with every toy.'
        : 'Water-based lube is safe with every toy, including silicone (silicone lube can damage silicone toys).';
      pairGrid.innerHTML = pickL.map((r, i) => this.#card(r, i)).join('');
      this.pairs.hidden = false;
    }

    /** @param {HTMLButtonElement} button */
    async #add(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      const handle = button.dataset.quizAdd;
      const say = (m) => { const s = button.closest('nhi-products')?.querySelector('.nhi-status') || this.status; if (s) { s.textContent = ''; requestAnimationFrame(() => { s.textContent = m; }); } };
      try {
        const pr = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        const product = await pr.json();
        const variant = (product.variants || []).find((v) => v.available);
        if (!variant) { say('Sorry, this one has just sold out.'); return; }
        if (product.variants.length > 1 && product.variants.filter((v) => v.available).length > 1 && product.options?.length && product.options[0] !== 'Title') {
          // several options: let the shopper choose on the product page
          window.location.href = `/products/${encodeURIComponent(handle)}`;
          return;
        }
        const url = window.theme?.routes?.cartAdd || '/cart/add.js';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variant.id, quantity: 1 }),
        });
        const data = await res.json();
        if (!res.ok || data.status) { say(data.description || data.message || 'Sorry, that could not be added.'); return; }
        say('Added to your bag');
        button.classList.add('is-added');
        button.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data, addToCartBtn: button }, bubbles: true }));
        if (window.theme?.settings?.cartType === 'page') window.location.href = window.theme.routes.cartPage;
      } catch {
        window.location.href = `/products/${encodeURIComponent(handle)}`;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };
  define('nhi-section', class extends NhiSection {});
  define('nhi-hero', NhiHero);
  define('nhi-panels', NhiPanels);
  define('nhi-products', NhiProducts);
  define('nhi-finder', NhiFinder);
  define('nhi-story', NhiStory);
  define('nhi-marquee', NhiMarquee);
  define('nhi-quiz', NhiQuiz);
})();
